import Ember from 'ember';
import { restartableTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import InputFilter from 'volta/components/filters/input-filter';
import SelectFilter from 'volta/components/filters/select-filter';
import { OrderStatusesOptions } from 'volta/models/constants/order-statuses';
import OrderModel, { ResourceName } from 'volta/models/order';
import { defaultErrorHandler } from 'volta/utils/error-utils';
import { eqFilter, ltFilter } from 'volta/utils/filters-utils';
import { isNaN } from 'volta/utils/math-utils';

import { action } from '@ember/object';
import { sort } from '@ember/object/computed';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type OpenOrdersCollectionDefinitionBuilder from 'volta/services/collection-builder-open-orders';
import type BvMetricsService from 'volta/services/bv-metrics';
import type IntlService from 'ember-intl/services/intl';
import type LabelService from 'volta/services/label-service';
import type SkuModalService from 'volta/services/sku-modal-service';
import type StoreService from 'volta/services/-store';
import type RouterService from '@ember/routing/router-service';
interface IArgs {
  skuId: string;
  workshopId?: string;
  dueDate?: Date;
}

export default class SkuDetailsOpenOrders extends Component<IArgs> {
  // Services
  // ~~~~

  @service store!: StoreService;
  @service router!: RouterService;
  @service intl!: IntlService;
  @service labelService!: LabelService;
  @service skuModalService!: SkuModalService;
  @service bvMetrics!: BvMetricsService;
  @service collectionBuilderOpenOrders!: OpenOrdersCollectionDefinitionBuilder;

  // Properties
  // ~~~~

  @tracked orders: OrderModel[] = [];
  @tracked sorts = [{ valuePath: 'dueDate', isAscending: true }];
  @tracked searchQuery = '';
  @tracked filterValues: { [key: string]: TFilterValue } = {};
  @tracked filteredOrders: OrderModel[] = [];

  resourceName = ResourceName;

  // Computed Properties
  // ~~~~~

  @sort('filteredOrders', 'sortProperties')
  sortedOrders!: OrderModel[];

  get sortProperties() {
    return this.sorts.map((s) => {
      return `${s.valuePath}:${s.isAscending ? 'asc' : 'desc'}`;
    });
  }

  get columns() {
    return this.collectionBuilderOpenOrders.buildColumns();
  }

  get filterDefinitions() {
    const defs: Array<TFilterDefinition | TTagsFilterDefinition> = [
      {
        queryParam: 'q',
        apiParam: 'q',
        comparators: ['lk'],
        component: InputFilter,
        hidden: true
      },
      {
        queryParam: 'qty',
        apiParam: 'qty',
        comparators: ['lt', 'eq', 'gt'],
        component: InputFilter,
        inputType: 'number',
        isPrimary: true
      },
      {
        queryParam: 'status',
        apiParam: 'status',
        comparators: ['in'],
        component: SelectFilter,
        possibleValues: OrderStatusesOptions,
        isPrimary: true
      }
    ];

    return defs;
  }

  // Lifecycle Hooks
  // ~~~~

  @action
  fetchOpenOrders() {
    this.getOpenOrdersTask.perform(this.args.skuId, this.args.dueDate, this.args.workshopId);
  }

  @action
  onSearchChange(filterValues: { [key: string]: TFilterValue }, query: string): void {
    this.searchQuery = query;
    const hasFilters = Object.keys(filterValues).length;
    if (query) {
      const orders = hasFilters ? this.filteredOrders : this.orders;
      this.filteredOrders = orders.filter((order) => {
        const filters = [order.orderNum, order.voltaOrderNum, order.vendorCode, order.orderType];
        return filters.some(
          (filter) => filter && filter.toLowerCase().includes(query.toLowerCase())
        );
      });
    } else {
      this.filteredOrders = hasFilters ? this.onFilterChange(filterValues) : this.orders;
    }
  }

  @action
  onFilterChange(filters: { [key: string]: TFilterValue }) {
    this.filterValues = filters;

    this.filteredOrders = this.orders.filter((order) => {
      const { qty, status } = this.filterValues;
      let isIncluded = true;
      const hasQty = qty?.values.length && !isNaN(+qty?.values[0]);
      if (qty && hasQty) {
        switch (qty.comparator) {
          case 'lt':
            isIncluded = hasQty ? order.qty < +qty.values[0] : true;
            break;
          case 'gt':
            isIncluded = hasQty ? order.qty > +qty.values[0] : true;
            break;
          case 'eq':
            isIncluded = hasQty ? order.qty === +qty.values[0] : true;
            break;
          default:
            isIncluded = true;
        }
      }

      if (status && status.values.length && !status.values.includes(order.status)) {
        isIncluded = false;
      }

      return isIncluded;
    });

    return this.filteredOrders;
  }

  // Actions
  // ~~~~~

  getOpenOrdersTask = restartableTask(
    async (skuId: string, dueDate?: Date, workshopId?: string) => {
      try {
        if (!skuId) {
          return;
        }

        const query: any = {
          filter: {
            skuId: eqFilter(skuId)
          },
          include: 'sku',
          sort: 'dueDate',
          page: { limit: 500, offset: 0 }
        };

        if (dueDate) {
          query.filter.dueDate = moment.utc().isBefore(dueDate)
            ? eqFilter(moment.utc(dueDate).format('YYYY-MM-DD'))
            : ltFilter(moment.utc(dueDate).add(1, 'd').format('YYYY-MM-DD'));
        }

        if (workshopId) {
          query.filter.workshopId = eqFilter(workshopId);
        }

        const orders: Ember.Array<OrderModel> = await this.store.query('order', query);
        this.orders = orders.toArray();
        this.filteredOrders = this.orders;
        return this.orders;
      } catch (error) {
        return defaultErrorHandler(error);
      }
    }
  );
}
